import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";

const title = "Pupils Needing Extra Support | Swedish International School";
const description =
  "How Swedish International School Estepona works with pupils needing extra support. Include relevant information with your application; each case is assessed.";

export const Route = createFileRoute("/en/extra-support")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/extra-support") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/extra-support"),
  }),
  component: ExtraSupport,
});

function ExtraSupport() {
  return (
    <>
      <PageHero
        eyebrow="Admissions"
        title="Pupils needing extra support"
        intro="Swedish International School Estepona (Marbella) strives to be an inclusive school. Before admission, we openly assess whether our resources can meet each pupil's needs safely and sustainably."
      />

      <Prose>
        <p>
          It is a small school with limited resources and premises, which limits the extent to which
          the needs of pupils requiring special support can be met. It is therefore important to
          include all relevant information with your application.
        </p>
        <p>
          The cost of any extra materials, aids and additional teaching resources is covered by
          guardians. Admission of a pupil needing extra support is assessed by the management team and
          discussed with the guardian on a case-by-case basis.
        </p>
        <p>
          Don't hesitate to get in touch with any questions at{" "}
          <a href="mailto:info@sweintschool.com">info@sweintschool.com</a>.
        </p>

        <h2>How the assessment works</h2>
        <p>
          When an application includes information about previous support, assessments or diagnoses,
          the management team reviews the documentation together with the staff concerned. We look at
          what support the pupil has had, what has worked well and what would be required in our
          setting. We then arrange a conversation with guardians to go through what the school can
          realistically offer and where the limits lie.
        </p>
        <p>
          We would rather be clear than promise too much. As a small school we do not have all the
          resources a larger organisation has, so sometimes the answer is that we cannot meet the need
          well. An early, honest conversation serves the child better than a school start that does
          not hold.
        </p>

        <h2>What we need from you</h2>
        <ul>
          <li>Educational assessments, support plans or equivalent from the previous school.</li>
          <li>Any reports or certificates relevant to schooling.</li>
          <li>Your own description of what works for your child in a school environment.</li>
          <li>Details of aids or adaptations used today.</li>
        </ul>

        <h2>Follow-up during schooling</h2>
        <p>
          Needs change over time. Class teachers and the{" "}
          <a className="text-primary link-quiet" href="/en/student-health-team">student health team</a>{" "}
          follow up adaptations continuously and check in with home at development meetings or more
          often when needed. If you feel something isn't working, contact the class teacher directly —
          that is always the right way in.
        </p>
      </Prose>

      <FaqBlock items={faqFor("extra-stod", "en")} locale={"en"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
